import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { MarkdownEditor } from "@/components/app/MarkdownEditor";
import { ShieldAlert, Loader2, ArrowLeft, ExternalLink, Save, CheckCircle2, Sparkles, ChevronDown, ChevronUp, Copy, Search } from "lucide-react";
import { toast } from "sonner";
import { apiErrorText } from "@/lib/apiError";
import { useAuth } from "@/context/AuthContext";

const CURRICULA = [
  { key: "a_level", label: "A-Level", typical_placeholder: "AAB" },
  { key: "ib", label: "IB (points)", typical_placeholder: "34" },
  { key: "cbse", label: "CBSE (%)", typical_placeholder: "82" },
  { key: "american_diploma", label: "American Diploma (GPA)", typical_placeholder: "3.4" },
  { key: "french_bac", label: "French Bac", typical_placeholder: "Mention Bien" },
];

// Researcher form: form fields mirror the shape of apply_verified_corrections.py
// so the AI-interpretation cache is cleared identically on save.
export default function AdminVerifyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [state, setState] = useState({ loading: true, forbidden: false, degree: null });
  const [form, setForm] = useState(_emptyForm());
  const [saving, setSaving] = useState(false);
  const [aiFetch, setAiFetch] = useState({ open: true, urls: ["", "", ""], sources: [null, null, null], loading: false, result: null, error: null });
  const [suggesting, setSuggesting] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const r = await api.get(`/admin/verify/${encodeURIComponent(id)}`);
      setState({ loading: false, forbidden: false, degree: r.data });
      setForm(_fromDegree(r.data));
      // Pre-populate the AI-Fetch source URLs from whatever evidence is
      // already stored on the record. Nothing hits the LLM — it's a
      // pure data read that keeps the editor from re-pasting URLs.
      try {
        const s = await api.get(`/admin/verify/${encodeURIComponent(id)}/suggest-urls`);
        const existing = (s.data?.existing || []).slice(0, 3);
        if (existing.length > 0) {
          const urls = ["", "", ""];
          const sources = [null, null, null];
          existing.forEach((row, i) => { urls[i] = row.url; sources[i] = "existing"; });
          setAiFetch((prev) => ({ ...prev, urls, sources, open: true }));
        }
      } catch { /* best-effort — don't block the page */ }
    } catch (err) {
      const status = err?.response?.status;
      setState({ loading: false, forbidden: status === 401 || status === 403, degree: null });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    if (!form.source_url || !form.source_label) {
      toast.error("Evidence URL + source label are required (they flip the Verified pill).");
      return;
    }
    setSaving(true);
    try {
      const body = _toPatch(form);
      const r = await api.post(`/admin/verify/${encodeURIComponent(id)}`, body);
      toast.success(`Verified — patched ${r.data?.fields_patched} fields.`);
      nav("/admin/verify");
    } catch (err) {
      toast.error(apiErrorText(err, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  const runAiFetch = async () => {
    const urls = aiFetch.urls.map((u) => u.trim()).filter(Boolean);
    if (urls.length === 0) {
      toast.error("Add at least one source URL first.");
      return;
    }
    setAiFetch((s) => ({ ...s, loading: true, error: null, result: null }));
    try {
      const r = await api.post(`/admin/verify/${encodeURIComponent(id)}/ai-fetch`, { urls });
      if (!r.data?.ok) {
        setAiFetch((s) => ({ ...s, loading: false, error: r.data?.reason || "Extraction failed", result: r.data }));
        toast.error(r.data?.reason || "AI extraction failed — check your source URLs");
        return;
      }
      const firstOk = (r.data.sources || []).find((s) => s.ok);
      setForm((f) => {
        const next = { ...f };
        if (!f.source_url && firstOk?.url) next.source_url = firstOk.url;
        if (!f.source_label && firstOk?.title) next.source_label = firstOk.title.slice(0, 250);
        return next;
      });
      setAiFetch((s) => ({ ...s, loading: false, error: null, result: r.data }));
      toast.success("Candidate values ready — review each field and click Use to apply.");
    } catch (err) {
      setAiFetch((s) => ({ ...s, loading: false, error: apiErrorText(err, "Fetch failed"), result: null }));
      toast.error(apiErrorText(err, "AI-Fetch failed"));
    }
  };

  const suggestUrls = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get(`/admin/verify/${encodeURIComponent(id)}/suggest-urls`);
      const existing = data?.existing || [];
      const suggested = data?.suggested || [];
      // Merge: keep existing entries in place, fill blanks with suggestions.
      setAiFetch((prev) => {
        const urls = [...prev.urls];
        const sources = [...prev.sources];
        // First pass: place existing (if not already present)
        for (const row of existing) {
          if (urls.includes(row.url)) continue;
          const slot = urls.findIndex((u) => !u);
          if (slot === -1) break;
          urls[slot] = row.url;
          sources[slot] = "existing";
        }
        // Second pass: fill remaining blanks with AI guesses
        for (const row of suggested) {
          if (urls.includes(row.url)) continue;
          const slot = urls.findIndex((u) => !u);
          if (slot === -1) break;
          urls[slot] = row.url;
          sources[slot] = "ai_suggested";
        }
        return { ...prev, urls, sources, open: true };
      });
      const total = existing.length + suggested.length;
      if (total === 0) {
        toast.error(
          data?.allowed_domains?.length
            ? "No candidate URLs found — paste one manually."
            : "This university has no official_domains set — ask an admin to seed one first."
        );
      } else {
        toast.success(`Filled ${existing.length} existing + ${suggested.length} AI-suggested URL(s). Review before extracting.`);
      }
    } catch (err) {
      toast.error(apiErrorText(err, "URL suggestion failed"));
    } finally {
      setSuggesting(false);
    }
  };

  const applyCandidate = (field, value) => {
    setForm((f) => ({ ...f, [field]: value == null ? "" : String(value) }));
    toast.success(`Applied ${field} = ${value}`);
  };

  const applyEntryCandidate = (curr, sub, value) => {
    setForm((f) => ({
      ...f,
      entry: { ...f.entry, [curr]: { ...f.entry[curr], [sub]: value == null ? "" : String(value) } },
    }));
    toast.success(`Applied ${curr}.${sub} = ${value}`);
  };

  if (state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm" data-testid="admin-verify-detail-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading record…
      </div>
    );
  }
  if (state.forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="admin-verify-detail-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Admin access required</h1>
          {!user && <Button className="mt-4" onClick={login} data-testid="admin-verify-detail-signin-btn">Sign in with Google</Button>}
        </div>
      </div>
    );
  }
  if (!state.degree) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16">
        <p className="text-stone-500">Degree not found.</p>
        <Link to="/admin/verify" className="text-sm underline">Back to queue</Link>
      </div>
    );
  }

  const d = state.degree;
  const verified = d.content_verified;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-verify-detail-page">
      <Link to="/admin/verify" className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-900" data-testid="admin-verify-back-link">
        <ArrowLeft className="h-4 w-4" /> Back to queue
      </Link>

      <header className="mt-4 mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-editorial text-3xl">{d.official_title}</h1>
          <p className="mt-1 text-stone-500 font-mono text-xs">{d.id}</p>
          <p className="mt-2 text-sm text-stone-600">
            {d.university_id} · {d.campus || "—"} · {d.qualification || "—"}
          </p>
        </div>
        <div className="text-right">
          {verified ? (
            <span className="inline-flex items-center gap-1 text-xs font-mono text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5" /> Verified
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-mono text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
              Unverified
            </span>
          )}
          <div className="mt-2">
            <a
              href={`/degree/${d.id}`}
              target="_blank"
              rel="noreferrer"
              className="text-xs inline-flex items-center gap-1 text-stone-600 hover:text-stone-900"
              data-testid="admin-verify-open-live-link"
            >
              Open live page <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </header>

      <div className="grid md:grid-cols-2 gap-5">
        {/* AI-Fetch — assisted extraction */}
        <section className="md:col-span-2 p-5 border border-indigo-200 rounded-lg bg-indigo-50/40" data-testid="admin-verify-aifetch-section">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-editorial text-lg flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-indigo-700" /> AI-Fetch (assisted verification)
              </h2>
              <p className="mt-1 text-xs text-stone-600 max-w-2xl">
                Paste 1–3 official-page URLs (course page, admissions handbook, fees page).
                Claude will extract candidate values with quotes and confidence. You review
                each one — nothing writes to the DB until you click Save.
              </p>
            </div>
            <button
              type="button"
              className="text-xs font-underground-sc tracking-wider text-stone-500 hover:text-stone-900 flex items-center gap-1"
              onClick={() => setAiFetch((s) => ({ ...s, open: !s.open }))}
              data-testid="admin-verify-aifetch-toggle"
            >
              {aiFetch.open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              {aiFetch.open ? "Hide" : "Open"}
            </button>
          </div>

          {aiFetch.open && (
            <div className="mt-4 space-y-3">
              {aiFetch.urls.map((u, i) => (
                <div key={i}>
                  <Label className="text-[10px] font-underground-sc tracking-wider text-stone-500 flex items-center gap-2">
                    <span>Source URL {i + 1} {i === 0 ? "· primary (course page)" : "· optional"}</span>
                    {aiFetch.sources[i] === "existing" && (
                      <span
                        data-testid={`admin-verify-aifetch-url-badge-${i}`}
                        className="text-[9px] font-underground-sc tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-1.5 py-0.5"
                      >existing evidence</span>
                    )}
                    {aiFetch.sources[i] === "ai_suggested" && (
                      <span
                        data-testid={`admin-verify-aifetch-url-badge-${i}`}
                        className="text-[9px] font-underground-sc tracking-wider bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-full px-1.5 py-0.5"
                      >AI-suggested · verify</span>
                    )}
                  </Label>
                  <Input
                    type="url"
                    placeholder={i === 0 ? "https://www.uni.edu/programmes/bsc-computer-science" : i === 1 ? "https://www.uni.edu/admissions/international-fees" : "https://www.uni.edu/admissions/entry-requirements"}
                    value={u}
                    onChange={(e) => {
                      const next = [...aiFetch.urls];
                      next[i] = e.target.value;
                      // Editing clears the "existing/AI-suggested" badge —
                      // whatever the user types is theirs, not ours.
                      const nextSources = [...aiFetch.sources];
                      nextSources[i] = null;
                      setAiFetch((s) => ({ ...s, urls: next, sources: nextSources }));
                    }}
                    data-testid={`admin-verify-aifetch-url-${i}`}
                  />
                </div>
              ))}
              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  size="sm"
                  onClick={runAiFetch}
                  disabled={aiFetch.loading}
                  data-testid="admin-verify-aifetch-run-btn"
                >
                  {aiFetch.loading ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Extracting…</>
                  ) : (
                    <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Extract with AI</>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={suggestUrls}
                  disabled={suggesting}
                  data-testid="admin-verify-aifetch-suggest-btn"
                >
                  {suggesting ? (
                    <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Searching…</>
                  ) : (
                    <><Sparkles className="h-3.5 w-3.5 mr-1.5" /> Suggest URLs with AI</>
                  )}
                </Button>
                {aiFetch.error && <span className="text-xs text-red-700">{aiFetch.error}</span>}
              </div>

              {aiFetch.result?.sources?.length > 0 && (
                <div className="mt-2 text-[11px] font-mono text-stone-500" data-testid="admin-verify-aifetch-sources">
                  <div className="uppercase tracking-wider text-stone-400 mb-1">Fetched sources</div>
                  <ul className="space-y-0.5">
                    {aiFetch.result.sources.map((s, i) => (
                      <li key={i} className="flex items-center gap-2">
                        {s.ok ? <CheckCircle2 className="h-3 w-3 text-emerald-600" /> : <ShieldAlert className="h-3 w-3 text-red-600" />}
                        <a href={s.url} target="_blank" rel="noreferrer" className="underline hover:text-stone-900 truncate max-w-[560px]">{s.url}</a>
                        {!s.ok && <span className="text-red-700">— {s.error}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {(() => {
                // Broken-URL rescue panel: when any fetched source failed
                // (404/403/timeout), give the editor a pre-built Google
                // search that scopes to the uni's allowlisted domain, so
                // they can find a working URL in one click.
                const failed = (aiFetch.result?.sources || []).filter((s) => !s.ok);
                if (failed.length === 0) return null;
                const uniName = state.degree?.university_name || state.degree?.university_id || "";
                const degName = state.degree?.official_title || "";
                const failedHosts = Array.from(new Set(
                  failed
                    .map((s) => {
                      try { return new URL(s.url).host.replace(/^www\./, ""); }
                      catch { return null; }
                    })
                    .filter(Boolean)
                ));
                // Root domain for `site:` — strip any leading `www.` and
                // keep the last 2-3 labels so `cs.uni-bonn.de` still
                // targets `uni-bonn.de`. We keep as-is for .ac.uk / .edu.au
                // multi-part TLDs since Google matches suffix.
                const searches = failedHosts.flatMap((host) => [
                  {
                    label: "Course page",
                    q: `site:${host} "${degName}"`,
                  },
                  {
                    label: "International fees",
                    q: `site:${host} "${degName}" international tuition fees`,
                  },
                  {
                    label: "Entry requirements",
                    q: `site:${host} "${degName}" entry requirements admission`,
                  },
                ]);
                return (
                  <div
                    className="mt-3 p-4 bg-amber-50/60 border border-amber-200 rounded-md"
                    data-testid="admin-verify-aifetch-rescue"
                  >
                    <div className="flex items-start gap-2">
                      <Search className="h-4 w-4 text-amber-800 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-amber-900">
                          {failed.length} URL{failed.length > 1 ? "s" : ""} couldn't be fetched — find the real one:
                        </div>
                        <p className="mt-1 text-[11px] text-amber-900/80 leading-relaxed">
                          Click a search below to open Google scoped to the university's own domain.
                          When you find the right page, copy its URL back into a source slot above
                          and re-run <em>Extract with AI</em>.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {searches.map((s, i) => (
                            <a
                              key={i}
                              href={`https://www.google.com/search?q=${encodeURIComponent(s.q)}`}
                              target="_blank"
                              rel="noreferrer"
                              data-testid={`admin-verify-aifetch-rescue-search-${i}`}
                              className="inline-flex items-center gap-1.5 text-[11px] font-underground-sc tracking-wider bg-white border border-amber-300 text-amber-900 rounded-full px-2.5 py-1 hover:bg-amber-100"
                            >
                              <Search className="h-3 w-3" />
                              {s.label}
                              <ExternalLink className="h-3 w-3 opacity-60" />
                            </a>
                          ))}
                        </div>
                        <div className="mt-2 text-[10px] font-mono text-amber-900/70">
                          Query hint: <span className="italic">{searches[0]?.q}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {aiFetch.result?.candidate && (
                <div className="mt-3 space-y-2" data-testid="admin-verify-aifetch-candidates">
                  <div className="text-[10px] font-underground-sc tracking-widest text-indigo-800">
                    Candidate values — click Use to apply
                  </div>
                  {Object.entries(aiFetch.result.candidate.fields || {}).map(([k, v]) => (
                    v?.value == null ? null : (
                      <CandidateRow
                        key={k}
                        field={k}
                        value={v.value}
                        quote={v.quote}
                        source={v.source_url}
                        confidence={v.confidence}
                        onApply={() => applyCandidate(k, v.value)}
                      />
                    )
                  ))}
                  {Object.entries(aiFetch.result.candidate.entry_requirements || {}).map(([curr, v]) => {
                    if (!v || (v.typical == null && v.minimum == null)) return null;
                    return (
                      <div key={curr} className="p-3 bg-white border border-stone-200 rounded-md">
                        <div className="text-[10px] font-underground-sc tracking-wider text-stone-500 mb-1">{curr}</div>
                        {["typical", "minimum"].map((sub) => (
                          v[sub] == null ? null : (
                            <CandidateRow
                              key={sub}
                              field={`${curr}.${sub}`}
                              value={v[sub]}
                              quote={v.quote}
                              source={v.source_url}
                              confidence={v.confidence}
                              onApply={() => applyEntryCandidate(curr, sub, v[sub])}
                            />
                          )
                        ))}
                      </div>
                    );
                  })}
                  {aiFetch.result.candidate.notes && (
                    <div className="mt-2 text-[11px] italic text-stone-600" data-testid="admin-verify-aifetch-notes">
                      AI note: {aiFetch.result.candidate.notes}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </section>

        {/* Evidence source */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-evidence-section">
          <h2 className="font-editorial text-lg mb-3">Evidence source <span className="text-red-500 text-sm">*</span></h2>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Official admissions URL</Label>
              <Input
                type="url"
                placeholder="https://www.university.edu/admissions/programme"
                value={form.source_url}
                onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                data-testid="admin-verify-source-url-input"
              />
            </div>
            <div>
              <Label className="text-xs">Source label / citation</Label>
              <Input
                placeholder="Uni X — BSc Programme admissions requirements"
                value={form.source_label}
                onChange={(e) => setForm({ ...form, source_label: e.target.value })}
                data-testid="admin-verify-source-label-input"
              />
            </div>
            <div>
              <Label className="text-xs">Academic year</Label>
              <Input
                placeholder="2026-27"
                value={form.academic_year}
                onChange={(e) => setForm({ ...form, academic_year: e.target.value })}
                data-testid="admin-verify-academic-year-input"
              />
            </div>
          </div>
        </section>

        {/* Admissions */}
        <section className="p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-admissions-section">
          <h2 className="font-editorial text-lg mb-3">Admissions</h2>
          <div>
            <Label className="text-xs">Offer rate (0–1)</Label>
            <Input
              type="number" step="0.01" min="0" max="1"
              placeholder="e.g. 0.35"
              value={form.offer_rate}
              onChange={(e) => setForm({ ...form, offer_rate: e.target.value })}
              data-testid="admin-verify-offer-rate-input"
            />
          </div>
          <div className="mt-3">
            <Label className="text-xs">Admissions note (optional)</Label>
            <Input
              placeholder="e.g. Non-EU deadline 1 April; portfolio required"
              value={form.admissions_note}
              onChange={(e) => setForm({ ...form, admissions_note: e.target.value })}
              data-testid="admin-verify-admissions-note-input"
            />
          </div>
        </section>

        {/* Tuition */}
        <section className="p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-tuition-section">
          <h2 className="font-editorial text-lg mb-3">Tuition</h2>
          <div>
            <Label className="text-xs">International tuition (USD/year)</Label>
            <Input
              type="number" min="0"
              placeholder="e.g. 18500"
              value={form.tuition_international_usd}
              onChange={(e) => setForm({ ...form, tuition_international_usd: e.target.value })}
              data-testid="admin-verify-tuition-input"
            />
          </div>
          <p className="text-xs text-stone-500 mt-2">
            Convert EUR/GBP/AED to USD at 2026 rates (approx: €1=$1.09, £1=$1.27, AED 3.67 = $1).
          </p>
        </section>

        {/* English */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-english-section">
          <h2 className="font-editorial text-lg mb-3">English proficiency</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs">IELTS typical</Label>
              <Input type="number" step="0.5" min="0" max="9"
                placeholder="6.5" value={form.ielts_typical}
                onChange={(e) => setForm({ ...form, ielts_typical: e.target.value })}
                data-testid="admin-verify-ielts-typical-input" />
            </div>
            <div>
              <Label className="text-xs">IELTS min (per band)</Label>
              <Input type="number" step="0.5" min="0" max="9"
                placeholder="6.0" value={form.ielts_min}
                onChange={(e) => setForm({ ...form, ielts_min: e.target.value })}
                data-testid="admin-verify-ielts-min-input" />
            </div>
            <div>
              <Label className="text-xs">TOEFL typical</Label>
              <Input type="number" min="0" max="120"
                placeholder="90" value={form.toefl_typical}
                onChange={(e) => setForm({ ...form, toefl_typical: e.target.value })}
                data-testid="admin-verify-toefl-typical-input" />
            </div>
            <div>
              <Label className="text-xs">TOEFL min</Label>
              <Input type="number" min="0" max="120"
                placeholder="80" value={form.toefl_min}
                onChange={(e) => setForm({ ...form, toefl_min: e.target.value })}
                data-testid="admin-verify-toefl-min-input" />
            </div>
          </div>
          <div className="mt-3">
            <Label className="text-xs">English note (optional — e.g. IELTS Online NOT accepted)</Label>
            <Input value={form.english_note}
              onChange={(e) => setForm({ ...form, english_note: e.target.value })}
              data-testid="admin-verify-english-note-input" />
          </div>
          <p className="text-xs text-stone-500 mt-2">
            Leave IELTS at 0 for German/French-taught programmes where DSH-2/DELF is the actual screen.
          </p>
        </section>

        {/* Entry requirements per curriculum */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-entry-section">
          <h2 className="font-editorial text-lg mb-1">Entry requirements</h2>
          <p className="text-[10px] font-underground-sc tracking-wider text-stone-400 mb-3">
            Dashed rows have <span className="text-amber-700">no saved data</span> — the greyed values are placeholder examples, not the DB
          </p>
          <div className="space-y-4">
            {CURRICULA.map((c) => {
              const row = form.entry[c.key] || {};
              const isEmpty = !row.typical && !row.minimum && !row.min_maths && !row.required_subjects;
              const inputCls = isEmpty
                ? "border-dashed border-amber-300 bg-amber-50/30 placeholder:text-amber-400/60"
                : "";
              return (
                <div
                  key={c.key}
                  data-testid={`admin-verify-entry-row-${c.key}`}
                  data-empty={isEmpty ? "true" : "false"}
                  className={`grid grid-cols-1 md:grid-cols-5 gap-2 items-end ${isEmpty ? "opacity-95" : ""}`}
                >
                  <div className="text-sm font-mono text-stone-600 md:pt-6 flex items-center gap-2">
                    <span>{c.label}</span>
                    {isEmpty && (
                      <span
                        data-testid={`admin-verify-entry-not-set-${c.key}`}
                        className="inline-flex items-center rounded-full border border-amber-300 bg-amber-50 text-amber-800 text-[9px] font-underground-sc tracking-wider px-1.5 py-[1px]"
                        title="No saved data in the DB — the field values shown are input placeholders."
                      >
                        Not set
                      </span>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Typical</Label>
                    <Input
                      placeholder={c.typical_placeholder}
                      value={row.typical}
                      onChange={(e) => _setEntry(setForm, c.key, "typical", e.target.value)}
                      data-testid={`admin-verify-${c.key}-typical-input`}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Minimum</Label>
                    <Input
                      placeholder="ABB / 30 / 70"
                      value={row.minimum}
                      onChange={(e) => _setEntry(setForm, c.key, "minimum", e.target.value)}
                      data-testid={`admin-verify-${c.key}-minimum-input`}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Min Maths</Label>
                    <Input
                      placeholder="A / 6"
                      value={row.min_maths}
                      onChange={(e) => _setEntry(setForm, c.key, "min_maths", e.target.value)}
                      data-testid={`admin-verify-${c.key}-min-maths-input`}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Required subjects (csv)</Label>
                    <Input
                      placeholder="Maths, Physics"
                      value={row.required_subjects}
                      onChange={(e) => _setEntry(setForm, c.key, "required_subjects", e.target.value)}
                      data-testid={`admin-verify-${c.key}-required-input`}
                      className={inputCls}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-stone-500 mt-3">
            Leave a row blank if the university doesn't publish that curriculum. Only rows with a "typical" or "minimum" are patched.
          </p>
        </section>

        {/* Title override */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-title-section">
          <h2 className="font-editorial text-lg mb-3">Title override (optional)</h2>
          <Input
            placeholder={`Currently: "${d.official_title}"`}
            value={form.official_title}
            onChange={(e) => setForm({ ...form, official_title: e.target.value })}
            data-testid="admin-verify-title-override-input"
          />
          <p className="text-xs text-stone-500 mt-2">
            Only set if the seed used a wrong programme name (e.g. "Data Engineering" → "Electronics and Data Engineering (BEng)").
          </p>
        </section>

        {/* Basics — qualification, campus, duration */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-basics-section">
          <h2 className="font-editorial text-lg mb-3">Basics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Qualification</Label>
              <Input
                value={form.qualification}
                onChange={(e) => setForm({ ...form, qualification: e.target.value })}
                placeholder="BA (Hons), MEng, LLB…"
                data-testid="admin-verify-qualification-input"
              />
            </div>
            <div>
              <Label className="text-xs">Campus</Label>
              <Input
                value={form.campus}
                onChange={(e) => setForm({ ...form, campus: e.target.value })}
                placeholder="main campus"
                data-testid="admin-verify-campus-input"
              />
            </div>
            <div>
              <Label className="text-xs">Duration (years)</Label>
              <Input
                type="number"
                step="0.5"
                min="1"
                max="10"
                value={form.duration_years}
                onChange={(e) => setForm({ ...form, duration_years: e.target.value })}
                data-testid="admin-verify-duration-input"
              />
            </div>
          </div>
        </section>

        {/* Fees */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white" data-testid="admin-verify-fees-section">
          <h2 className="font-editorial text-lg mb-3">Fees</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Domestic tuition (GBP/yr)</Label>
              <Input
                type="number"
                value={form.tuition_domestic_annual_gbp}
                onChange={(e) => setForm({ ...form, tuition_domestic_annual_gbp: e.target.value })}
                data-testid="admin-verify-tuition-domestic-input"
              />
            </div>
            <div>
              <Label className="text-xs">Fee year (e.g. 2027/28)</Label>
              <Input
                value={form.fee_year_label}
                onChange={(e) => setForm({ ...form, fee_year_label: e.target.value })}
                data-testid="admin-verify-fee-year-input"
              />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Fee note (public — displayed under the fee)</Label>
              <Textarea
                rows={2}
                value={form.tuition_domestic_note}
                onChange={(e) => setForm({ ...form, tuition_domestic_note: e.target.value })}
                data-testid="admin-verify-fee-note-input"
                maxLength={500}
              />
            </div>
          </div>
        </section>

        {/* Narrative — 6 flagship sections */}
        <section className="md:col-span-2 p-5 border border-stone-200 rounded-lg bg-white space-y-4" data-testid="admin-verify-narrative-section">
          <h2 className="font-editorial text-lg">Narrative — Six flagship sections</h2>
          <p className="text-xs text-stone-500 -mt-2">
            Content shown on the public course page. Markdown supported (headings, bold, bullets). Untouched sections keep the AI-drafted version.
          </p>
          {[
            { key: "summary", label: "Summary" },
            { key: "admissions", label: "Admissions" },
            { key: "cost", label: "Cost" },
            { key: "curriculum", label: "Curriculum" },
            { key: "outcomes", label: "Outcomes" },
            { key: "international", label: "International" },
          ].map((s) => {
            const prov = (d?.ai_interpretation?.section_provenance || {})[s.key];
            return (
              <div key={s.key}>
                <div className="flex items-baseline justify-between gap-3">
                  <Label className="text-xs uppercase tracking-wider">{s.label}</Label>
                  <div className="text-[10px] font-mono text-stone-500" data-testid={`admin-verify-ai-provenance-${s.key}`}>
                    {prov?.edited_by ? (
                      <span className="text-emerald-700">
                        Edited by {prov.edited_by}
                        {prov.edited_at ? ` · ${new Date(prov.edited_at).toLocaleDateString()}` : ""}
                      </span>
                    ) : d?.ai_interpretation?.model ? (
                      <span>
                        AI draft ({(d.ai_interpretation.model || "").split(":").pop()})
                        {d.ai_interpretation.generated_at ? ` · ${new Date(d.ai_interpretation.generated_at).toLocaleDateString()}` : ""}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="mt-1">
                  <MarkdownEditor
                    value={form[`ai_${s.key}`] ?? ""}
                    onChange={(v) => setForm((f) => ({ ...f, [`ai_${s.key}`]: v }))}
                    testId={`admin-verify-ai-${s.key}`}
                    rows={5}
                  />
                </div>
              </div>
            );
          })}
        </section>
      </div>

      <div className="mt-8 flex items-center justify-end gap-3" data-testid="admin-verify-actions">
        <Link to="/admin/verify">
          <Button variant="outline" data-testid="admin-verify-cancel-btn">Cancel</Button>
        </Link>
        <Button onClick={save} disabled={saving} data-testid="admin-verify-save-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save & mark verified
        </Button>
      </div>
    </div>
  );
}

// ---- Form helpers ----------------------------------------------------------
function _emptyEntry() { return { typical: "", minimum: "", min_maths: "", required_subjects: "" }; }
function _emptyForm() {
  return {
    source_url: "",
    source_label: "",
    academic_year: "2026-27",
    offer_rate: "",
    tuition_international_usd: "",
    tuition_domestic_annual_gbp: "",
    fee_year_label: "",
    tuition_domestic_note: "",
    duration_years: "",
    campus: "",
    qualification: "",
    ielts_typical: "",
    ielts_min: "",
    toefl_typical: "",
    toefl_min: "",
    admissions_note: "",
    english_note: "",
    official_title: "",
    ai_summary: "",
    ai_admissions: "",
    ai_cost: "",
    ai_curriculum: "",
    ai_outcomes: "",
    ai_international: "",
    entry: Object.fromEntries(CURRICULA.map((c) => [c.key, _emptyEntry()])),
  };
}
function _setEntry(setForm, key, field, value) {
  setForm((f) => ({
    ...f, entry: { ...f.entry, [key]: { ...f.entry[key], [field]: value } },
  }));
}

// Pre-fill form from the existing degree record so researchers only type what's missing.
function _fromDegree(d) {
  const f = _emptyForm();
  const cost = d?.cost || {};
  const ai = d?.ai_interpretation || {};
  f.offer_rate = d?.admissions?.offer_rate ?? "";
  f.tuition_international_usd = d?.tuition_international_usd ?? cost.tuition_intl_annual_usd ?? "";
  f.tuition_domestic_annual_gbp = cost.tuition_domestic_annual_gbp ?? "";
  f.fee_year_label = cost.fee_year_label || "";
  f.tuition_domestic_note = cost.tuition_domestic_note || "";
  f.duration_years = d?.duration_years ?? "";
  f.campus = d?.campus || "";
  f.qualification = d?.qualification || "";
  f.ielts_typical = d?.english_test?.ielts_typical ?? "";
  f.ielts_min = d?.english_test?.ielts_min ?? "";
  f.toefl_typical = d?.english_test?.toefl_typical ?? "";
  f.toefl_min = d?.english_test?.toefl_min ?? "";
  f.official_title = d?.official_title || "";
  f.ai_summary = ai.summary || "";
  f.ai_admissions = ai.admissions || "";
  f.ai_cost = ai.cost || "";
  f.ai_curriculum = ai.curriculum || "";
  f.ai_outcomes = ai.outcomes || "";
  f.ai_international = ai.international || "";
  const er = d?.entry_requirements || {};
  CURRICULA.forEach((c) => {
    const r = er[c.key] || {};
    f.entry[c.key] = {
      typical: r.typical != null ? String(r.typical) : "",
      minimum: r.minimum != null ? String(r.minimum) : "",
      min_maths: r.min_maths != null ? String(r.min_maths) : "",
      required_subjects: (r.required_subjects || []).join(", "),
    };
  });
  return f;
}

function _toPatch(form) {
  const patch = {
    source_url: form.source_url.trim(),
    source_label: form.source_label.trim(),
    academic_year: form.academic_year.trim() || "2026-27",
  };
  if (form.offer_rate !== "") patch.offer_rate = parseFloat(form.offer_rate);
  if (form.tuition_international_usd !== "") patch.tuition_international_usd = parseInt(form.tuition_international_usd, 10);
  if (form.tuition_domestic_annual_gbp !== "") patch.tuition_domestic_annual_gbp = parseInt(form.tuition_domestic_annual_gbp, 10);
  if (form.fee_year_label.trim()) patch.fee_year_label = form.fee_year_label.trim();
  if (form.tuition_domestic_note.trim()) patch.tuition_domestic_note = form.tuition_domestic_note.trim();
  if (form.duration_years !== "") patch.duration_years = parseFloat(form.duration_years);
  if (form.campus.trim()) patch.campus = form.campus.trim();
  if (form.qualification.trim()) patch.qualification = form.qualification.trim();
  if (form.ielts_typical !== "") { patch.ielts_typical = parseFloat(form.ielts_typical); patch.ielts_min = form.ielts_min !== "" ? parseFloat(form.ielts_min) : patch.ielts_typical; }
  if (form.toefl_typical !== "") { patch.toefl_typical = parseInt(form.toefl_typical, 10); patch.toefl_min = form.toefl_min !== "" ? parseInt(form.toefl_min, 10) : patch.toefl_typical; }
  if (form.admissions_note.trim()) patch.admissions_note = form.admissions_note.trim();
  if (form.english_note.trim()) patch.english_note = form.english_note.trim();
  if (form.official_title.trim()) patch.official_title = form.official_title.trim();

  // Narrative — only send sections that have content
  const aiKeys = ["summary", "admissions", "cost", "curriculum", "outcomes", "international"];
  const aiPatch = {};
  for (const k of aiKeys) {
    const v = form[`ai_${k}`];
    if (v !== undefined && v !== null && String(v).trim() !== "") aiPatch[k] = String(v).trim();
  }
  if (Object.keys(aiPatch).length > 0) patch.ai_interpretation = aiPatch;

  const er = {};
  for (const c of CURRICULA) {
    const r = form.entry[c.key];
    if (!r.typical.trim() && !r.minimum.trim()) continue;
    er[c.key] = {
      typical: _num(r.typical) ?? r.typical.trim() ?? null,
      minimum: _num(r.minimum) ?? r.minimum.trim() ?? null,
      min_maths: r.min_maths.trim() || null,
      required_subjects: r.required_subjects.trim() ? r.required_subjects.split(",").map(s => s.trim()).filter(Boolean) : [],
    };
  }
  if (Object.keys(er).length) patch.entry_requirements = er;
  return patch;
}
function _num(s) {
  if (!s || !s.trim()) return null;
  const n = Number(s);
  return Number.isFinite(n) && String(n) === s.trim() ? n : null;
}

function CandidateRow({ field, value, quote, source, confidence, onApply }) {
  const badge =
    confidence === "high" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : confidence === "medium" ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-stone-50 text-stone-700 border-stone-200";
  return (
    <div
      className="p-3 bg-white border border-stone-200 rounded-md text-xs"
      data-testid={`admin-verify-aifetch-candidate-${field}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-underground-sc tracking-wider text-[10px] text-stone-500">{field}</span>
            <span className="font-mono text-stone-900 truncate">{String(value)}</span>
            {confidence && (
              <span className={`text-[10px] font-underground-sc tracking-wider border rounded-full px-1.5 py-0.5 ${badge}`}>
                {confidence}
              </span>
            )}
          </div>
          {quote && (
            <div className="mt-1 text-stone-600 italic line-clamp-2">
              &ldquo;{quote}&rdquo;
            </div>
          )}
          {source && (
            <a
              href={source}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono text-indigo-700 hover:text-indigo-900 underline truncate max-w-full"
            >
              <ExternalLink className="h-3 w-3 shrink-0" />
              <span className="truncate">{source}</span>
            </a>
          )}
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={onApply}
          data-testid={`admin-verify-aifetch-use-${field}`}
          className="shrink-0"
        >
          <Copy className="h-3 w-3 mr-1" /> Use
        </Button>
      </div>
    </div>
  );
}

